import React, { useState, useEffect, useContext } from 'react';
import Flow from '../../../../Assets/Images/leadership-flow.png';
import HeaderPreview from '../../../GeneratePdf/HeaderPreview';
import FooterPreview from '../../../GeneratePdf/FooterPreview';
import GroupReportContext from '../../../../Context/GroupReportContext';
import { applyFullTieOrder } from '../../../../utils/quadrantTieOrder';

// Hierarchy for tie-breaking: lower number = higher priority
const LEADERSHIP_HIERARCHY = {
  collaborative: 0,  // Highest priority
  visionary: 1,
  strategic: 2,
  performance: 3    // Lowest priority
};

// Comparator function for sorting leadership quadrants with tie-breaking
const compareLeadershipQuadrants = (a, b) => {
  // First compare by score (descending)
  const scoreDiff = b.score - a.score;
  if (scoreDiff !== 0) return scoreDiff;
  
  // If scores are equal, compare by hierarchy (ascending - lower number = higher priority)
  const aHierarchy = LEADERSHIP_HIERARCHY[a.name.toLowerCase()] ?? 999;
  const bHierarchy = LEADERSHIP_HIERARCHY[b.name.toLowerCase()] ?? 999;
  return aHierarchy - bHierarchy;
};

// Comparator for objects with 'name' and 'value' properties
const compareLeadershipByValue = (a, b) => {
  // First compare by value (descending)
  const valueDiff = b.value - a.value;
  if (valueDiff !== 0) return valueDiff;
  
  // If values are equal, compare by hierarchy
  const aHierarchy = LEADERSHIP_HIERARCHY[a.name.toLowerCase()] ?? 999;
  const bHierarchy = LEADERSHIP_HIERARCHY[b.name.toLowerCase()] ?? 999;
  return aHierarchy - bHierarchy;
};

const LeadershipFlowGroup = ({ apiData, previewData, reportType, pageNumber }) => {

  const { averagePercentagesLeadership } = useContext(GroupReportContext)

  const styles = {
    page: { padding: '16px 0px', backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', fontFamily: 'Helvetica', },
    header: { display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #00577D', paddingBottom: '5px', marginBottom: '2px', marginTop: '-15px', },
    titleMain: { fontSize: '12px', fontWeight: '700', color: '#76A3BE', },
    title: { fontSize: '11px', fontWeight: '200', color: '#76A3BE', },
    rightSection: { textAlign: 'right', },
    rightText: { fontSize: '8px', fontWeight: '700', color: '#76A3BE', },
    container: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', },
    headerText: { fontSize: '11px', margin: 0, paddingBottom: "16px", lineHeight: '15px', fontWeight: '700', color: '#00577D',  },
    image: { width: '297px', height: '314px', objectFit: 'contain', },
    paragraph: { fontSize: '16px', lineHeight: '24px', fontWeight: '400', color: '#245775', },
    paragraphtopspace: { fontSize: '16px', lineHeight: '24px', fontWeight: '400', color: '#245775', },
    footer: { width: '100%', borderTop: '1px solid #00577D', paddingTop: '5px', display: 'flex', justifyContent: 'space-between', position: 'absolute', bottom: '15px', left: '21px', right: '21px', },
    footerLeft: { display: 'flex', flexDirection: 'column', },
    footerTextHeading: { fontSize: '10px', fontWeight: '700', color: '#76A3BE', },
    footerText: { fontSize: '8px', fontWeight: '400', color: '#76A3BE', maxWidth: '400px', },
  };

  const [sortedScores, setSortedScores] = useState([0, 0, 0, 0]);
  const [sortedScoresLabel, setSortedScoresLabel] = useState([
    { name: 'Strategic', value: 0 },
    { name: 'Visionary', value: 0 },
    { name: 'Collaborative', value: 0 },
    { name: 'Performance', value: 0 },
  ]);

  useEffect(() => {
    if (averagePercentagesLeadership) {
      const labels = [
        { name: 'Strategic', value: averagePercentagesLeadership[0]?.avg },
        { name: 'Visionary', value: averagePercentagesLeadership[1]?.avg },
        { name: 'Collaborative', value: averagePercentagesLeadership[2]?.avg },
        { name: 'Performance', value: averagePercentagesLeadership[3]?.avg },
      ];
      const sortedLabels = [...labels].sort(compareLeadershipByValue);
      const sortedValues = sortedLabels.map(l => (Number(l.value.toFixed(2)) || 0));
      setSortedScoresLabel(sortedLabels);
    }
  }, [averagePercentagesLeadership]);

  const SquareWithArrow = ({ scores }) => {
    const topLeft = 'STRATEGIC';
    const topRight = 'PERFORMANCE';
    const bottomLeft = 'VISIONARY';
    const bottomRight = 'COLLABORATIVE';

    const topLeftValue = (scores?.find(s => s.name === 'strategic')?.value || 0).toFixed(2);
    const topRightValue = (scores?.find(s => s.name === 'performance')?.value || 0).toFixed(2);
    const bottomLeftValue = (scores?.find(s => s.name === 'visionary')?.value || 0).toFixed(2);
    const bottomRightValue = (scores?.find(s => s.name === 'collaborative')?.value || 0).toFixed(2);

    const labelStyle = { color: '#000', fontSize: '9px', fontWeight: 700, fontFamily: 'Helvetica, sans-serif', textTransform: 'uppercase', position: 'absolute', lineHeight: '13px' };
    const percentageStyle = { color: '#000', fontSize: '9px', fontWeight: 700, fontFamily: 'Helvetica, sans-serif', position: 'absolute', lineHeight: '13px' };

    const diagramSize = 240;
    const headLength = 16;
    const headWidth = 10;
    const centerOffsetX = diagramSize / 2;
    const centerOffsetY = diagramSize / 2;
    const quadrantOffsetX = centerOffsetX / 2;
    const quadrantOffsetY = centerOffsetY / 2;

    const quadrantCenters = {
      strategic: { x: quadrantOffsetX, y: quadrantOffsetY },
      performance: { x: diagramSize - quadrantOffsetX, y: quadrantOffsetY },
      visionary: { x: quadrantOffsetX, y: diagramSize - quadrantOffsetY },
      collaborative: { x: diagramSize - quadrantOffsetX, y: diagramSize - quadrantOffsetY }
    };

    const quadrantScores = [
      { name: 'strategic', score: scores?.find(s => s.name === 'strategic')?.value || 0 },
      { name: 'performance', score: scores?.find(s => s.name === 'performance')?.value || 0 },
      { name: 'visionary', score: scores?.find(s => s.name === 'visionary')?.value || 0 },
      { name: 'collaborative', score: scores?.find(s => s.name === 'collaborative')?.value || 0 }
    ];

    const sortedQuadrants = applyFullTieOrder([...quadrantScores].sort(compareLeadershipQuadrants), (q) => q.name, (q) => q.score)
      .map(({ name, score }) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: score
      }));

    const pathPoints = sortedQuadrants.map(step => quadrantCenters[step.name.toLowerCase()]);

    let arrowPath = '';
    if (pathPoints.length > 0) {
      const pointsForPath = [...pathPoints];
      if (pointsForPath.length > 1) {
        const tip = pointsForPath[pointsForPath.length - 1];
        const prev = pointsForPath[pointsForPath.length - 2];
        const dx = tip.x - prev.x;
        const dy = tip.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const trimmedTip = {
          x: tip.x - (headLength * dx) / len,
          y: tip.y - (headLength * dy) / len
        };
        pointsForPath[pointsForPath.length - 1] = trimmedTip;
      }

      arrowPath = `M ${pointsForPath[0].x} ${pointsForPath[0].y}`;
      for (let i = 1; i < pointsForPath.length; i++) {
        arrowPath += ` L ${pointsForPath[i].x} ${pointsForPath[i].y}`;
      }
    }

    const dotPosition = sortedQuadrants[0]
      ? quadrantCenters[sortedQuadrants[0].name.toLowerCase()]
      : { x: 0, y: 0 };

    const arrowColor = '#B6111F';
    let arrowHeadPoints = '';

    if (sortedQuadrants.length > 1) {
      const lastIdx = sortedQuadrants.length - 1;
      const tip = quadrantCenters[sortedQuadrants[lastIdx].name.toLowerCase()];
      const prev = quadrantCenters[sortedQuadrants[lastIdx - 1].name.toLowerCase()];
      const angle = Math.atan2(tip.y - prev.y, tip.x - prev.x);
      const left = {
        x: tip.x - headLength * Math.cos(angle) + headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) - headWidth * Math.cos(angle)
      };
      const right = {
        x: tip.x - headLength * Math.cos(angle) - headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) + headWidth * Math.cos(angle)
      };
      arrowHeadPoints = `${tip.x},${tip.y} ${left.x},${left.y} ${right.x},${right.y}`;
    }

    return (
      <div style={{ position: 'relative', width: `${diagramSize}px`, height: `${diagramSize}px`, backgroundColor: 'transparent', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '2px solid #000' }}>

        <div style={{ ...labelStyle, top: '6px', left: '10px' }}>
          {topLeft}
        </div>
        <div style={{ ...labelStyle, top: '6px', right: '10px' }}>
          {topRight}
        </div>

        <div style={{ ...percentageStyle, top: '20px', left: '10px' }}>
          {topLeftValue}%
        </div>

        <div style={{ ...percentageStyle, top: '20px', right: '10px' }}>
          {topRightValue}%
        </div>

        <div style={{ ...percentageStyle, bottom: '20px', left: '10px' }}>
          {bottomLeftValue}%
        </div>
        <div style={{ ...labelStyle, bottom: '6px', left: '10px' }}>
          {bottomLeft}
        </div>

        <div style={{ ...percentageStyle, bottom: '20px', right: '10px' }}>
          {bottomRightValue}%
        </div>
        <div style={{ ...labelStyle, bottom: '6px', right: '10px' }}>
          {bottomRight}
        </div>

        <div style={{ position: 'absolute', width: '2px', height: '100%', backgroundColor: '#000' }}></div>
        <div style={{ position: 'absolute', width: '100%', height: '2px', backgroundColor: '#000' }}></div>

        <svg width={diagramSize} height={diagramSize} style={{ position: 'absolute', top: 0, left: 0 }}>
          <circle cx={dotPosition.x} cy={dotPosition.y} r="8.5" fill={arrowColor} />
          <path
            d={arrowPath}
            stroke={arrowColor}
            strokeWidth="4"
            strokeOpacity="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          {arrowHeadPoints && (
            <polygon points={arrowHeadPoints} fill={arrowColor} stroke={arrowColor} />
          )}
        </svg>
      </div>
    );
  };


  return (
    <div>
      <HeaderPreview />
      <div style={styles.page}>
        <div>
          <div style={styles.container}>
            <div style={styles.headerText}>GROUP LEADERSHIP FLOW</div>
            <SquareWithArrow
              scores={sortedScoresLabel.map((item) => ({
                name: item.name.toLowerCase(),
                value: item.value
              }))}
            />
          </div>
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
};

export default LeadershipFlowGroup;
